/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  GenericObject,
  MaintenanceSchedule,
  ServiceItem,
  ServiceLog
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageState} from '@/types/pages';
import {AdminAction, AsyncGridState, AsyncState} from '../../types/redux';
import {
  ADD_SERVICE_LOG,
  ADD_SERVICE_LOG_ERROR,
  ADD_SERVICE_LOG_REQUEST,
  ADD_SERVICE_LOG_RESPONSE,
  DELETE_HISTORY,
  DELETE_HISTORY_ERROR,
  DELETE_HISTORY_REQUEST,
  DELETE_HISTORY_RESPONSE,
  EDIT_SERVICE_LOG,
  EDIT_SERVICE_LOG_ERROR,
  EDIT_SERVICE_LOG_REQUEST,
  EDIT_SERVICE_LOG_RESPONSE,
  GET_MAINTENANCE_SCHEDULES_ERROR,
  GET_MAINTENANCE_SCHEDULES_REQUEST,
  GET_MAINTENANCE_SCHEDULES_RESPONSE,
  GET_SCHEDULES_SERVICE_STATUS_ERROR,
  GET_SCHEDULES_SERVICE_STATUS_REQUEST,
  GET_SCHEDULES_SERVICE_STATUS_RESPONSE,
  GET_SERVICE_LOGS_ERROR,
  GET_SERVICE_LOGS_REQUEST,
  GET_SERVICE_LOGS_RESPONSE,
  GET_SERVICE_STATUS_ERROR,
  GET_SERVICE_STATUS_REQUEST,
  GET_SERVICE_STATUS_RESPONSE,
  INITIALIZE_MAINTENANCE,
  MAINTENANCE_HISTORY_GRID_REFRESHED,
  SET_HISTORY_GRID_COLUMNS,
  SET_MAINTENANCE_DASHBOARD_GRID_FILTER_MODEL,
  SET_MAINTENANCE_GRID_COLUMNS,
  SET_MAINTENANCE_HISTORY_GRID_FILTER_MODEL,
  SET_MAINTENANCE_SCHEDULE_GRID_FILTER_MODEL,
  SET_SCHEDULE_GRID_COLUMNS,
  SET_SELECTED_MAINTENANCE_SCHEDULE,
  UPDATE_ASSET_REFERENCE_ERROR,
  UPDATE_ASSET_REFERENCE_REQUEST,
  UPDATE_ASSET_REFERENCE_RESPONSE,
  UPDATE_HISTORY_GRID_COLUMNS,
  UPDATE_MAINTENANCE_GRID_COLUMNS,
  UPDATE_SCHEDULE_GRID_COLUMNS,
  VIEW_SERVICE_HISTORY,
  VIEW_SERVICE_HISTORY_EDITOR,
  VIEW_SERVICE_HISTORY_ERROR,
  VIEW_SERVICE_HISTORY_REQUEST,
  VIEW_SERVICE_HISTORY_RESPONSE,
  VIEW_SERVICE_STATUS,
  VIEW_SERVICE_STATUS_ERROR,
  VIEW_SERVICE_STATUS_REQUEST,
  VIEW_SERVICE_STATUS_RESPONSE
} from '../actions/maintenance';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

interface MaintenanceAction extends AdminAction {
  serviceItem?: ServiceItem | ServiceLog;
}

interface MaintenanceActionState extends AsyncState {
  selected: ServiceItem | ServiceItem[] | ServiceLog | ServiceItem[] | null;
  result: ServiceItem | ServiceItem[] | ServiceLog | ServiceItem[] | null;
  isEditing: boolean;
};

type MaintenanceDeleteAction = Omit<MaintenanceActionState, 'isEditing'> & {isDeleting: boolean};

export interface MaintenanceState extends PageState {
  dashboard?: {
    loading: boolean;
    error: ServiceError;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {}
  },
  scheduleGrid?: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {}
  },
  historyGrid?: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {}
  },
  selected?: {
    assetSchedule: MaintenanceSchedule;
    schedule: AsyncGridState,
    history: AsyncGridState
  },
  viewServiceStatus: MaintenanceActionState;
  viewServiceHistory: MaintenanceActionState;
  addServiceLog: MaintenanceActionState;
  deleteHistory: MaintenanceDeleteAction;
  editServiceLog: MaintenanceActionState;
  updateAssetReference: {
    loading: boolean;
    error: ServiceError;
  },
  scheduleService?: {
    result: Array<GenericObject>;
    loading: Boolean;
    error: ServiceError;
  }
}

// defines the initial state for the reducer ...
export const initialState: MaintenanceState = {
  isInitialized : false,
  dashboard     : {
    loading : false,
    error   : null
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  scheduleGrid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  historyGrid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  selected : {
    assetSchedule : null,
    schedule      : {
      loading     : false,
      columnState : null,
      error       : null
    },
    history : {
      loading     : false,
      columnState : null,
      error       : null
    }
  },
  viewServiceStatus : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  viewServiceHistory : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  deleteHistory : {
    isDeleting : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null
  },
  addServiceLog : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  editServiceLog : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  updateAssetReference : {
    loading : false,
    error   : null
  },
  scheduleService : {
    result  : null,
    loading : false,
    error   : null
  }
};

// defines the user preference key for the maintenance dashboard page ...
const MAINTENANCE_PREFERENCE          = 'maintenance';
const MAINTENANCE_HISTORY_PREFERENCE  = 'maintenance-history';
const MAINTENANCE_SCHEDULE_PREFERENCE = 'maintenance-schedule';

// defines the reducers ...
const reducers = {
  [INITIALIZE_MAINTENANCE] : draft => {
    draft.isInitialized            = true;
    // main grid
    const maintenancePreference    = getGridColumnState(MAINTENANCE_PREFERENCE, true);
    draft.grid.columnState         = maintenancePreference;
    draft.grid.preferedColumnState = maintenancePreference;

    // history grid
    const historyPreference               = getGridColumnState(MAINTENANCE_HISTORY_PREFERENCE, true);
    draft.historyGrid.columnState         = historyPreference;
    draft.historyGrid.preferedColumnState = historyPreference;

    // schedule grid
    const schedulePreference               = getGridColumnState(MAINTENANCE_SCHEDULE_PREFERENCE, true);
    draft.scheduleGrid.columnState         = schedulePreference;
    draft.scheduleGrid.preferedColumnState = schedulePreference;
  },
  [SET_MAINTENANCE_GRID_COLUMNS] : ({grid}: MaintenanceState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : false,
      preference : MAINTENANCE_PREFERENCE
    }, columnState);
  },
  [UPDATE_MAINTENANCE_GRID_COLUMNS] : ({grid}: MaintenanceState, {columnState}) => {
    onUpdateGridColumns({
      grid,
      preference : MAINTENANCE_PREFERENCE
    }, columnState);
  },
  [SET_SCHEDULE_GRID_COLUMNS] : ({scheduleGrid, selected: {schedule}}: MaintenanceState, {columnsState}) => {
    onSetGridColumns({
      grid       : scheduleGrid,
      isLoading  : schedule.loading,
      preference : MAINTENANCE_SCHEDULE_PREFERENCE
    }, columnsState);
  },
  [UPDATE_SCHEDULE_GRID_COLUMNS] : ({scheduleGrid}: MaintenanceState, {columnState}) => {
    const columnsState = {
      nextColumnState : columnState
    };
    onUpdateGridColumns({
      grid       : scheduleGrid,
      preference : MAINTENANCE_SCHEDULE_PREFERENCE
    }, columnsState);
  },
  [SET_HISTORY_GRID_COLUMNS] : ({historyGrid, selected: {history}}: MaintenanceState, {columnsState}) => {
    onSetGridColumns({
      grid       : historyGrid,
      isLoading  : history.loading,
      preference : MAINTENANCE_HISTORY_PREFERENCE
    }, columnsState);
  },
  [UPDATE_HISTORY_GRID_COLUMNS] : ({historyGrid}: MaintenanceState, {columnState}) => {
    const columnsState = {
      nextColumnState : columnState
    };
    onUpdateGridColumns({
      grid       : historyGrid,
      preference : MAINTENANCE_HISTORY_PREFERENCE
    }, columnsState);
  },
  [GET_MAINTENANCE_SCHEDULES_REQUEST] : ({dashboard}: MaintenanceState) => {
    dashboard.loading = true;
  },
  [GET_MAINTENANCE_SCHEDULES_RESPONSE] : ({dashboard}: MaintenanceState) => {
    dashboard.loading = false;
  },
  [GET_MAINTENANCE_SCHEDULES_ERROR] : ({dashboard}: MaintenanceState, {error}) => {
    dashboard.error = error;
    dashboard.loading = false;
  },
  [SET_SELECTED_MAINTENANCE_SCHEDULE] : ({selected}: MaintenanceState, {schedule}) => {
    selected.assetSchedule = schedule;
  },
  [GET_SERVICE_STATUS_REQUEST] : ({selected: {schedule}}: MaintenanceState) => {
    schedule.loading = true;
  },
  [GET_SERVICE_STATUS_RESPONSE] : ({selected: {schedule}}: MaintenanceState) => {
    schedule.loading = false;
  },
  [GET_SERVICE_STATUS_ERROR] : ({selected: {schedule}}: MaintenanceState, {error}) => {
    schedule.error = error;
    schedule.loading = false;
  },
  [GET_SERVICE_LOGS_REQUEST] : ({selected: {history}}: MaintenanceState) => {
    history.loading = true;
  },
  [GET_SERVICE_LOGS_RESPONSE] : ({selected: {history}}: MaintenanceState) => {
    history.loading = false;
  },
  [GET_SERVICE_LOGS_ERROR] : ({selected: {history}}: MaintenanceState, {error}) => {
    history.error = error;
    history.loading = false;
  },

  [VIEW_SERVICE_STATUS] : ({viewServiceStatus}: MaintenanceState, {show, serviceItem}: MaintenanceAction) => {
    viewServiceStatus.isEditing = show;
    viewServiceStatus.result    = null;
    viewServiceStatus.selected  = show ? serviceItem : null;
  },
  [VIEW_SERVICE_STATUS_REQUEST] : ({viewServiceStatus}: MaintenanceState) => {
    viewServiceStatus.loading = true;
    viewServiceStatus.error   = viewServiceStatus.result = null;
  },
  [VIEW_SERVICE_STATUS_RESPONSE] : (
    {viewServiceStatus}: MaintenanceState,
    {result}: MaintenanceAction
  ) => {
    viewServiceStatus.loading   = false;
    viewServiceStatus.result    = result;
  },
  [VIEW_SERVICE_STATUS_ERROR] : ({viewServiceStatus}: MaintenanceState, {error}: MaintenanceAction) => {
    viewServiceStatus.loading = false;
    viewServiceStatus.error   = error;
  },
  [DELETE_HISTORY] : ({deleteHistory}: MaintenanceState, {show, serviceItem}: MaintenanceAction) => {
    deleteHistory.result     = null;
    deleteHistory.isDeleting = show;
    deleteHistory.selected   = show ? serviceItem : null;
  },
  [DELETE_HISTORY_REQUEST] : ({deleteHistory}: MaintenanceState) => {
    deleteHistory.loading    = true;
    deleteHistory.error      = deleteHistory.result = null;
  },
  [DELETE_HISTORY_RESPONSE] : ({deleteHistory}: MaintenanceState, {result}: MaintenanceAction) => {
    deleteHistory.loading    = false;
    deleteHistory.isDeleting = false;
    deleteHistory.result     = result;
  },
  [DELETE_HISTORY_ERROR] : ({deleteHistory}: MaintenanceState, {error}: MaintenanceAction) => {
    deleteHistory.loading = false;
    deleteHistory.error   = error;
  },
  [MAINTENANCE_HISTORY_GRID_REFRESHED] : (
    {deleteHistory}: MaintenanceState
  ) => {
    deleteHistory.result     = null;
  },
  [VIEW_SERVICE_HISTORY] : (
    {viewServiceHistory}: MaintenanceState,
    {serviceItem}: MaintenanceAction
  ) => {
    viewServiceHistory.result    = null;
    viewServiceHistory.selected  = serviceItem;
  },
  [VIEW_SERVICE_HISTORY_EDITOR] : (
    {viewServiceHistory}: MaintenanceState,
    {show, serviceItem}: MaintenanceAction
  ) => {
    viewServiceHistory.isEditing = show;
    viewServiceHistory.result    = null;
    viewServiceHistory.selected  = show ? serviceItem : null;
  },
  [VIEW_SERVICE_HISTORY_REQUEST] : ({viewServiceHistory}: MaintenanceState) => {
    viewServiceHistory.loading = true;
    viewServiceHistory.error   = viewServiceHistory.result = null;
  },
  [VIEW_SERVICE_HISTORY_RESPONSE] : (
    {viewServiceHistory}: MaintenanceState,
    {result}: MaintenanceAction
  ) => {
    viewServiceHistory.loading   = false;
    viewServiceHistory.isEditing = false;
    viewServiceHistory.result    = result;
  },
  [VIEW_SERVICE_HISTORY_ERROR] : ({viewServiceHistory}: MaintenanceState, {error}: MaintenanceAction) => {
    viewServiceHistory.loading = false;
    viewServiceHistory.error   = error;
  },

  [ADD_SERVICE_LOG] : ({addServiceLog}: MaintenanceState, {show}: MaintenanceAction) => {
    addServiceLog.isEditing = show;
    addServiceLog.result    = null;
  },
  [ADD_SERVICE_LOG_REQUEST] : ({addServiceLog}: MaintenanceState) => {
    addServiceLog.loading = true;
    addServiceLog.error   = addServiceLog.result = null;
  },
  [ADD_SERVICE_LOG_RESPONSE] : ({addServiceLog}: MaintenanceState, {result}: MaintenanceAction) => {
    addServiceLog.loading   = false;
    addServiceLog.isEditing = false;
    addServiceLog.result    = result;
  },
  [ADD_SERVICE_LOG_ERROR] : ({addServiceLog}: MaintenanceState, {error}: MaintenanceAction) => {
    addServiceLog.loading   = false;
    addServiceLog.error     = error;
  },

  [EDIT_SERVICE_LOG] : ({editServiceLog}: MaintenanceState, {show, serviceItem}: MaintenanceAction) => {
    editServiceLog.selected  = show ? serviceItem : null;
    editServiceLog.isEditing = show;
    editServiceLog.result    = null;
  },
  [EDIT_SERVICE_LOG_REQUEST] : ({editServiceLog}: MaintenanceState) => {
    editServiceLog.loading = true;
    editServiceLog.error   = editServiceLog.result = null;
  },
  [EDIT_SERVICE_LOG_RESPONSE] : ({editServiceLog}: MaintenanceState, {result}: MaintenanceAction) => {
    editServiceLog.loading   = false;
    editServiceLog.isEditing = false;
    editServiceLog.result    = result;
  },
  [EDIT_SERVICE_LOG_ERROR] : ({editServiceLog}: MaintenanceState, {error}: MaintenanceAction) => {
    editServiceLog.loading   = false;
    editServiceLog.error     = error;
  },
  [UPDATE_ASSET_REFERENCE_REQUEST] : ({updateAssetReference}: MaintenanceState) => {
    updateAssetReference.loading = true;
    updateAssetReference.error   = null;
  },
  [UPDATE_ASSET_REFERENCE_RESPONSE] : ({updateAssetReference}: MaintenanceState) => {
    updateAssetReference.loading   = false;
  },
  [UPDATE_ASSET_REFERENCE_ERROR] : ({updateAssetReference}: MaintenanceState, {error}: MaintenanceAction) => {
    updateAssetReference.loading   = false;
    updateAssetReference.error     = error;
  },
  [GET_SCHEDULES_SERVICE_STATUS_REQUEST] : ({scheduleService}: MaintenanceState) => {
    scheduleService.result    = null;
    scheduleService.loading   = true;
  },
  [GET_SCHEDULES_SERVICE_STATUS_RESPONSE] : ({scheduleService}: MaintenanceState, {result}: MaintenanceAction) => {
    scheduleService.result    = result?.items || null;
    scheduleService.loading   = false;
  },
  [GET_SCHEDULES_SERVICE_STATUS_ERROR] : ({scheduleService}: MaintenanceState, {error}: MaintenanceAction) => {
    scheduleService.error    = error;
    scheduleService.loading   = false;
  },
  [SET_MAINTENANCE_DASHBOARD_GRID_FILTER_MODEL] : ({grid}: MaintenanceState, {model}) => {
    grid.filterModel = model;
  },
  [SET_MAINTENANCE_SCHEDULE_GRID_FILTER_MODEL] : ({scheduleGrid}: MaintenanceState, {model}) => {
    scheduleGrid.filterModel = model;
  },
  [SET_MAINTENANCE_HISTORY_GRID_FILTER_MODEL] : ({historyGrid}: MaintenanceState, {model}) => {
    historyGrid.filterModel = model;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: PageState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
